/**
 * Date and streak math for the daily writing tracker.
 *
 * Everything here is keyed on a "day key": the 'YYYY-MM-DD' the writer would read off their
 * own wall clock, never a UTC date. The client stamps every manuscript save with
 * formatLocalDate(new Date()) and the server stores that key verbatim, so a session that runs
 * from 11pm to 1am is split the way the writer experienced it rather than the way UTC did.
 * Date#toISOString() is the wrong tool for this and must not be used on these values.
 */

/** A day counts toward the streak once this many words land across all of a writer's stories. */
export const STREAK_THRESHOLD_WORDS = 500

/** The smallest per-story daily goal a writer may set. Mirrored server-side. */
export const MIN_DAILY_GOAL = 500

/** The largest per-story daily goal a writer may set. Mirrored server-side in writingProgress.ts. */
export const MAX_DAILY_GOAL = 100000

export interface WritingDay {
  /** Local calendar date, 'YYYY-MM-DD'. */
  day: string
  words: number
}

export interface WeekCell {
  /** Local calendar date, 'YYYY-MM-DD'. Always a real day — a week needs no padding. */
  day: string
  words: number
  hasMetThreshold: boolean
}

export function formatLocalDate(date: Date): string {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const dayOfMonth = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${dayOfMonth}`
}

function parseDayKey(day: string): Date {
  const [year, month, dayOfMonth] = day.split('-').map(Number)
  return new Date(year, month - 1, dayOfMonth)
}

export function addLocalDays(day: string, offset: number): string {
  const date = parseDayKey(day)
  date.setDate(date.getDate() + offset)
  return formatLocalDate(date)
}

function qualifyingDayKeys(days: WritingDay[]): Set<string> {
  return new Set(
    days.filter((entry) => entry.words >= STREAK_THRESHOLD_WORDS).map((entry) => entry.day)
  )
}

export function currentStreak(days: WritingDay[], today: string): number {
  const qualifying = qualifyingDayKeys(days)
  // A streak is still alive on a morning the writer hasn't started yet, so anchor on today
  // when it already qualifies and on yesterday otherwise.
  let cursor = qualifying.has(today) ? today : addLocalDays(today, -1)
  let streak = 0
  while (qualifying.has(cursor)) {
    streak += 1
    cursor = addLocalDays(cursor, -1)
  }
  return streak
}

export function longestStreak(days: WritingDay[]): number {
  const qualifying = [...qualifyingDayKeys(days)].sort()
  let longest = 0
  let run = 0
  let previous: string | null = null
  for (const day of qualifying) {
    run = previous !== null && addLocalDays(previous, 1) === day ? run + 1 : 1
    if (run > longest) longest = run
    previous = day
  }
  return longest
}

/** The Sunday that opens the week containing `day`. */
export function startOfWeek(day: string): string {
  // getDay() is already Sunday-first (0 = Sunday), so it is the offset back to the Sunday.
  return addLocalDays(day, -parseDayKey(day).getDay())
}

/**
 * The seven days of `anchor`'s week, Sunday through Saturday.
 *
 * Anchored on a day rather than on a Sunday so the caller can pass "today" directly; every
 * day of a week yields the same strip.
 */
export function buildWeek(anchor: string, days: WritingDay[]): WeekCell[] {
  const wordsByDay = new Map(days.map((entry) => [entry.day, entry.words]))
  const sunday = startOfWeek(anchor)

  return Array.from({ length: 7 }, (_unused, offset) => {
    const day = addLocalDays(sunday, offset)
    const words = wordsByDay.get(day) ?? 0
    return { day, words, hasMetThreshold: words >= STREAK_THRESHOLD_WORDS }
  })
}

/** e.g. "Wednesday, August 5". */
export function dayLabel(day: string): string {
  return parseDayKey(day).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric'
  })
}

/** e.g. "Aug 2 – 8", or "Jul 26 – Aug 1" when the week straddles two months. */
export function weekRangeLabel(anchor: string): string {
  const sunday = startOfWeek(anchor)
  const first = parseDayKey(sunday)
  const last = parseDayKey(addLocalDays(sunday, 6))
  const shortMonth = (date: Date): string => date.toLocaleDateString('en-US', { month: 'short' })

  return first.getMonth() === last.getMonth()
    ? `${shortMonth(first)} ${first.getDate()} – ${last.getDate()}`
    : `${shortMonth(first)} ${first.getDate()} – ${shortMonth(last)} ${last.getDate()}`
}
